/* ============
 * Pinia Store
 * ============
 */

import type { App } from 'vue'
import { createPinia, setActivePinia } from 'pinia'
import { markRaw } from 'vue'
import { useTrackingStore } from '@/store/modules/tracking'

const debug = import.meta.env.VITE_ENV !== 'production'
const persistedKey = 'pinia'

const pinia = createPinia()
setActivePinia(pinia)

let hydrationPerformed = false
let persistenceInitialized = false

const hydrateState = () => {
  if (hydrationPerformed) return

  try {
    const rawState = localStorage.getItem(persistedKey)
    if (!rawState) {
      hydrationPerformed = true
      return
    }

    const data = JSON.parse(rawState) as {
      tracking?: { trackingInfo: ReturnType<typeof useTrackingStore>['trackingInfo']; proofOfDelivery: string }
    }

    const trackingStore = useTrackingStore(pinia)

    if (data.tracking?.trackingInfo) {
      trackingStore.trackingInfo = data.tracking.trackingInfo
    }
    if (typeof data.tracking?.proofOfDelivery === 'string') {
      trackingStore.proofOfDelivery = data.tracking.proofOfDelivery
    }
  } catch (error) {
    if (debug) console.warn('Hydration error', error)
  } finally {
    hydrationPerformed = true
  }
}

const initializePersistence = () => {
  if (persistenceInitialized) return

  const trackingStore = useTrackingStore(pinia)

  const persistState = () => {
    try {
      const payload = {
        tracking: {
          trackingInfo: trackingStore.trackingInfo,
          proofOfDelivery: trackingStore.proofOfDelivery,
        },
      }
      localStorage.setItem(persistedKey, JSON.stringify(payload))
    } catch (error) {
      if (debug) console.warn('Persist state error', error)
    }
  }

  trackingStore.$subscribe(persistState, { detached: true })
  persistState()

  persistenceInitialized = true
}

const store = {
  install(app: App) {
    app.use(pinia)
    hydrateState()
    initializePersistence()
  },
  pinia,
}

export type Store = typeof store

export default markRaw(store)
